/* eslint-disable camelcase */

/*
 These are base configs for all metrics
 Any app can overwrite and extend any config
 Config specs: https://www.notion.so/corva/Drilling-Metrics-31de1dc4b7b448ec9e91ad4b61a941a2

 Fields:
    Required:
      label: UI metric name
      key: api key for metric

    Optional:
      unitType: the class of unit such as volume, length, mass, etc, used in convert.js
      from: the specific unit such as m, gal, lb, etc according to api
      to:  the unit that we want to convert the value to (on UI)
      precision: rounding api metric value, default is 2
      isBiggestBest: some metrics should be maximized, other - minimized
      customScale: function for scaling metrics, for example percents
      formatUnitDisplay: takes unitDisplay as argument
      allowNegativeValue: only a few metrics can be negative
      unitDisplay: the unit that we want to display on UI (for non-convertible metrics)

*/
import { getUnitPreference, getUnitDescription, convertValue } from '../convert';

import { convertPercentageMetrics, getSum, getAverage, getMax } from './utils';
import { metricsCategories } from './metricsCategories';

export const hole_depth = {
  label: 'Hole Depth',
  key: 'hole_depth',
  isBiggestBest: true,
  unitType: 'length',
  from: 'ft',
  convertForMultiRigs: getMax,
  category: metricsCategories.drilling_group,
};

export const hole_depth_change = {
  label: 'Total Distance Drilled',
  key: 'hole_depth_change',
  unitType: 'length',
  from: 'ft',
  isBiggestBest: true,
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const hole_depth_change_lateral = {
  label: 'Lateral Length',
  key: 'hole_depth_change_lateral',
  unitType: 'length',
  from: 'ft',
  isBiggestBest: true,
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const drilled_feet_rotary = {
  label: 'Rotary Distance Drilled',
  key: 'drilled_feet_rotary',
  unitType: 'length',
  from: 'ft',
  isBiggestBest: true,
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const drilled_feet_slide = {
  label: 'Slide Distance Drilled',
  key: 'drilled_feet_slide',
  unitType: 'length',
  from: 'ft',
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const drilled_feet_rotary_percentage = {
  label: 'Rotary %',
  key: 'drilled_feet_rotary_percentage',
  to: '%',
  customConvert: convertPercentageMetrics,
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.percent_group,
};
export const drilled_feet_slide_percentage = {
  label: 'Slide %',
  key: 'drilled_feet_slide_percentage',
  to: '%',
  customConvert: convertPercentageMetrics,
  convertForMultiRigs: getAverage,
  category: metricsCategories.percent_group,
};
export const drilling_percentage = {
  label: 'Drilling %',
  key: 'drilling_percentage',
  to: '%',
  customConvert: convertPercentageMetrics,
  isBiggestBest: true,
};
export const rop = {
  label: 'On-Bottom ROP',
  key: 'rop',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.rop_group,
};
export const rop_rotary = {
  label: 'Rotary ROP',
  key: 'rop_rotary',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.rop_group,
};
export const rop_slide = {
  label: 'Slide ROP',
  key: 'rop_slide',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.rop_group,
};
export const rop_net = {
  label: 'Net ROP',
  key: 'rop_net',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.rop_group,
};
export const rop_gross = {
  label: 'Gross ROP',
  key: 'rop_gross',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.rop_group,
};
export const feet_per_day = {
  label: 'Drilled Distance/Day',
  key: 'feet_per_day',
  unitType: 'length',
  from: 'ft',
  formatUnitDisplay: unitDisplay => `${unitDisplay}/day`,
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_group,
};
export const drilling_time_rotary = {
  label: 'Rotary Drilling Time',
  key: 'drilling_time_rotary',
  unitType: 'time',
  to: 'h',
  isBiggestBest: true,
};
export const drilling_time_slide = {
  label: 'Slide Drilling Time',
  key: 'drilling_time_slide',
  unitType: 'time',
  to: 'h',
};
export const on_bottom_time = {
  label: 'On Bottom Time',
  key: 'on_bottom_time',
  to: 'h',
  unitType: 'time',
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};
export const on_bottom_percentage = {
  label: 'On Bottom %',
  key: 'on_bottom_percentage',
  to: '%',
  customConvert: convertPercentageMetrics,
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.percent_group,
};
export const drilling_connection_average = {
  label: 'Slips to Slips',
  key: 'drilling_connection_average',
  to: 'min',
  unitType: 'time',
  from: 's',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_connection_group,
};
export const weight_to_weight_average = {
  label: 'Weight to Weight',
  key: 'weight_to_weight_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_connection_group,
};

export const bottom_to_slips_average = {
  label: 'Bottom to Slips',
  key: 'bottom_to_slips_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_connection_group,
};
export const slips_to_bottom_average = {
  label: 'Slips to Bottom',
  key: 'slips_to_bottom_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_connection_group,
};

export const pumps_on_to_bottom_average = {
  label: 'Pumps on to Bottom',
  key: 'pumps_on_to_bottom_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_connection_group,
};

export const tripping_in_speed = {
  label: 'Entire Hole Tripping In Speed',
  key: 'tripping_in_speed',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const tripping_out_speed = {
  label: 'Entire Hole Tripping Out Speed',
  key: 'tripping_out_speed',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};
export const tripping_in_speed_cased = {
  label: 'Cased Hole Tripping In Speed',
  key: 'tripping_in_speed_cased',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const tripping_out_speed_cased = {
  label: 'Cased Hole Tripping Out Speed',
  key: 'tripping_out_speed_cased',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};
export const tripping_in_speed_open = {
  label: 'Open Hole Tripping In Speed',
  key: 'tripping_in_speed_open',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const tripping_out_speed_open = {
  label: 'Open Hole Tripping Out Speed',
  key: 'tripping_out_speed_open',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};
export const tripping_in_time_cased = {
  label: 'Casing Tripping In Cased Hole',
  key: 'tripping_in_time_cased',
  to: 'min',
  from: 's',
  unitType: 'time',
};
export const tripping_in_time_open = {
  label: 'Casing Tripping In Open Hole',
  key: 'tripping_in_time_open',
  to: 'min',
  from: 's',
  unitType: 'time',
};
export const tripping_in_time = {
  label: 'Casing Tripping In Entire Hole',
  key: 'tripping_in_time',
  to: 'min',
  from: 's',
  unitType: 'time',
};
export const tripping_in_connection_time_cased = {
  label: 'Connection Time in Cased Hole',
  key: 'tripping_in_connection_time_cased',
  to: 'min',
  from: 's',
  unitType: 'time',
};
export const tripping_in_time_with_connection_open = {
  label: 'Casing Tripping In Open Hole + Connection Time in Open Hole',
  key: 'tripping_in_time_with_connection_open',
  to: 'min',
  from: 'h',
  unitType: 'time',
};
export const tripping_in_time_with_connection = {
  label: 'Casing Tripping In Entire Hole + Connection Time in Entire Hole',
  key: 'tripping_in_time_with_connection',
  to: 'min',
  from: 'h',
  unitType: 'time',
};
export const slips_to_slips = {
  label: 'Casing Slips to Slips',
  key: 'slips_to_slips_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_cased = {
  label: 'Casing - Cased Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed_cased',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_cased_std_hr = {
  label: 'Casing - Cased Hole Tripping In Speed (Joints/Hr, With Connection)',
  key: 'gross_tripping_in_speed_cased_std/hr',
  unitType: null,
  unitDisplay: 'joints/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_open = {
  label: 'Casing - Open Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed_open',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_open_std_hr = {
  label: 'Casing - Open Hole Tripping In Speed (Joints/Hr, With Connection)',
  key: 'gross_tripping_in_speed_open_std/hr',
  unitType: null,
  unitDisplay: 'joints/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed = {
  label: 'Casing - Entire Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_std_hr = {
  label: 'Casing - Entire Hole Tripping In Speed (Joints/Hr, With Connection)',
  key: 'gross_tripping_in_speed_std/hr',
  unitType: null,
  unitDisplay: 'joints/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.casing_group,
};
export const gross_tripping_in_speed_bha = {
  label: 'Entire Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const gross_tripping_in_speed_open_bha = {
  label: 'Open Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed_open_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const gross_tripping_in_speed_cased_bha = {
  label: 'Cased Hole Tripping In Speed (With Connection)',
  key: 'gross_tripping_in_speed_cased_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};
export const gross_tripping_out_speed_bha = {
  label: 'Entire Hole Tripping Out Speed (With Connection)',
  key: 'gross_tripping_out_speed_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};
export const gross_tripping_out_speed_open_bha = {
  label: 'Open Hole Tripping Out Speed (With Connection)',
  key: 'gross_tripping_out_speed_open_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};
export const gross_tripping_out_speed_cased_bha = {
  label: 'Cased Hole Tripping Out Speed (With Connection)',
  key: 'gross_tripping_out_speed_cased_bha',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};

export const gross_tripping_in_speed_total = {
  label: 'Gross Tripping In Speed',
  key: 'gross_tripping_in_speed_total',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
};

export const gross_tripping_out_speed_total = {
  label: 'Gross Tripping Out Speed',
  key: 'gross_tripping_out_speed_total',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};

export const cost_per_ft = {
  label: `Cost/${getUnitDescription(getUnitPreference('length')).singular}`,
  labelFunc: () => `Cost/${getUnitDescription(getUnitPreference('length')).singular}`,
  key: 'cost_per_ft',
  customConvert: value => value / convertValue(1, 'length', 'ft'),
  convertForMultiRigs: getAverage,
  category: metricsCategories.cost_group,
};

export const cost_per_lat_ft = {
  label: `Lateral Cost/${getUnitDescription(getUnitPreference('length')).singular}`,
  labelFunc: () => `Lateral Cost/${getUnitDescription(getUnitPreference('length')).singular}`,
  key: 'cost_per_lat_ft',
  customConvert: value => value / convertValue(1, 'length', 'ft'),
  convertForMultiRigs: getAverage,
  category: metricsCategories.cost_group,
};

export const npt = {
  label: 'NPT',
  key: 'npt',
  unitType: 'time',
  from: 'h',
  to: 'h',
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const circulating_time = {
  label: 'Circulating Time',
  key: 'circulating_time',
  unitType: 'time',
  from: 'h',
  to: 'h',
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_group,
};

export const spud_to_rig_release_time = {
  label: 'Spud to Rig Release',
  key: 'spud_to_rig_release_time',
  unitType: 'time',
  from: 'h',
  to: 'd',
  convertForMultiRigs: getMax,
  category: metricsCategories.spud_time_group,
};

export const spud_to_target = {
  label: 'Spud to Target',
  key: 'spud_to_target',
  unitType: 'time',
  from: 'h',
  to: 'd',
  convertForMultiRigs: getMax,
  category: metricsCategories.spud_time_group,
};

export const spud_to_total_depth_time = {
  label: 'Spud to Total Depth',
  key: 'spud_to_total_depth_time',
  unitType: 'time',
  from: 'h',
  to: 'd',
  convertForMultiRigs: getMax,
  category: metricsCategories.spud_time_group,
};

export const spud_to_landing_point = {
  label: 'Spud to Landing Point',
  key: 'spud_to_landing_point',
  unitType: 'time',
  from: 'h',
  to: 'd',
  convertForMultiRigs: getMax,
  category: metricsCategories.spud_time_group,
};

export const total_time = {
  label: 'Total Time',
  key: 'total_time',
  unitType: 'time',
  from: 'h',
  to: 'd',
};

export const drilling_time = {
  label: 'Drilling Time',
  key: 'drilling_time',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const tripping_time = {
  label: 'Tripping Time',
  key: 'tripping_time',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const gross_time = {
  label: 'Gross Time',
  key: 'gross_time',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const turn_rate = {
  label: 'Turn Rate',
  key: 'turn_rate',
  unitType: 'anglePerLength',
  from: 'dp100f',
  allowNegativeValue: true,
};

export const build_rate = {
  label: 'Build Rate',
  key: 'build_rate',
  unitType: 'anglePerLength',
  from: 'dp100f',
  allowNegativeValue: true,
};

// NOTE: Has no unit
export const cumulative_tortuosity = {
  label: 'Cumulative Tortuosity',
  key: 'cumulative_tortuosity',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_group,
};

export const weight_to_weight_count = {
  label: 'Drilling Stand Count',
  key: 'weight_to_weight_count',
  convertForMultiRigs: getSum,
  category: metricsCategories.drilling_connection_group,
};

export const tripping_connection_count = {
  label: 'Tripping Connection Count',
  key: 'tripping_connection_count',
  convertForMultiRigs: getSum,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_in_connection_count = {
  label: 'Tripping In Connection Count',
  key: 'tripping_in_connection_count',
  convertForMultiRigs: getSum,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_out_connection_count = {
  label: 'Tripping Out Connection Count',
  key: 'tripping_out_connection_count',
  convertForMultiRigs: getSum,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_in_speed_with_connection = {
  label: 'Tripping In Speed (With Connection)',
  key: 'tripping_in_speed_with_connection',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};

export const tripping_out_speed_with_connection = {
  label: 'Tripping Out Speed (With Connection)',
  key: 'tripping_out_speed_with_connection',
  unitType: 'velocity',
  from: 'ft/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};

export const slips_to_slips_count = {
  label: 'Casing Stand Count',
  key: 'slips_to_slips_count',
  convertForMultiRigs: getSum,
  category: metricsCategories.casing_group,
};

export const bha_count = {
  label: "Total BHA's",
  key: 'bha_count',
  category: metricsCategories.drilling_group,
};

export const gross_tripping_in_time_cased = {
  label: 'Cased Hole Tripping In Time (With Connection)',
  key: 'gross_tripping_in_time_cased',
  unitType: 'time',
  from: 'min',
};

export const gross_tripping_in_time_open = {
  label: 'Open Hole Tripping In Time (With Connection)',
  key: 'gross_tripping_in_time_open',
  unitType: 'time',
  from: 'min',
};

export const tripping_in_connection_time = {
  label: 'Tripping in (Connection)',
  key: 'tripping_in_connection_time',
  unitType: 'time',
  from: 'min',
};

export const tripping_out_connection_time = {
  label: 'Tripping out (Connection)',
  key: 'tripping_out_connection_time',
  unitType: 'time',
  from: 'min',
};

export const nu_nd_diverter_timelog_hal = {
  label: 'NU/ND Diverter',
  key: 'nu_nd_diverter_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};
export const ru_rd_casing_handling_equip_timelog_hal = {
  label: 'RU/RD Casing Handling Equip',
  key: 'ru_rd_casing_handling_equip_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const bop_wellhead_xmt_nu_nd_timelog_hal = {
  label: 'BOP/Wellhead/XMT NU/ND',
  key: 'bop_wellhead_xmt_nu_nd_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const bop_pressure_function_test_timelog_hal = {
  label: 'BOP Pressure & Function Test',
  key: 'bop_pressure_function_test_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const install_retrieve_wear_bushing_timelog_hal = {
  label: 'Install / Retrieve Wear Bushing',
  key: 'install_retrieve_wear_bushing_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const p_u_l_d_bha_timelog_hal = {
  label: 'P/U & L/D BHA',
  key: 'p_u_l_d_bha_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const drillout_casing_shoe_track_timelog_hal = {
  label: 'Drillout Casing Shoe Track',
  key: 'drillout_casing_shoe_track_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const slip_cut_timelog_hal = {
  label: 'Slip & Cut',
  key: 'slip_cut_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const survey_time_timelog_hal = {
  label: 'Survey Time',
  key: 'survey_time_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const drill_fit_lot_timelog_hal = {
  label: 'Drill & FIT/LOT',
  key: 'drill_fit_lot_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const pressure_test_timelog_hal = {
  label: 'Pressure Test',
  key: 'pressure_test_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ru_rd_mpd_system_or_rcd_timelog_hal = {
  label: 'RU/RD MPD System or RCD',
  key: 'ru_rd_mpd_system_or_rcd_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const achieve_frac_design_injection_rate_timelog_hal = {
  label: 'Achieve Frac Design Injection Rate',
  key: 'achieve_frac_design_injection_rate_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ru_rd_frac_head_timelog_hal = {
  label: 'RU/RD Frac Head',
  key: 'ru_rd_frac_head_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ru_rd_wireline_timelog_hal = {
  label: 'RU/RD Wireline',
  key: 'ru_rd_wireline_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ru_rd_lubricator_perf_guns_timelog_hal = {
  label: 'RU/RD Lubricator & Perf Guns',
  key: 'ru_rd_lubricator_perf_guns_timelog_hal',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const nu_nd_diverter_timelog_equinor = {
  label: 'NU/ND Diverter',
  key: 'nu_nd_diverter_timelog_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ru_casing_handling_equipment_equinor = {
  label: 'RU Casing Handling Equipment',
  key: 'ru_casing_handling_equipment_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const rd_casing_handling_equipment_equinor = {
  label: 'RD Casing Handling Equipment',
  key: 'rd_casing_handling_equipment_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const bop_wellhead_xmt_nu_nd_equinor = {
  label: 'BOP/Wellhead/XMT NU/ND',
  key: 'bop_wellhead_xmt_nu_nd_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const bop_test_equinor = {
  label: 'BOP Test',
  key: 'bop_test_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const install_wear_bushing_equinor = {
  label: 'Install Wear Bushing',
  key: 'install_wear_bushing_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const retrieve_wear_bushing_equinor = {
  label: 'Retrieve Wear Bushing',
  key: 'retrieve_wear_bushing_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_bha_equinor = {
  label: 'M/U BHA',
  key: 'mu_bha_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_bha_equinor = {
  label: 'L/D BHA',
  key: 'ld_bha_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const drillout_casing_shoe_track_equinor = {
  label: 'Drillout Casing Shoe Track',
  key: 'drillout_casing_shoe_track_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const slip_cut_equinor = {
  label: 'Slip & Cut',
  key: 'slip_cut_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const survey_time_equinor = {
  label: 'Survey Time',
  key: 'survey_time_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const drill_fit_lot_equinor = {
  label: 'Drill & FIT/LOT',
  key: 'drill_fit_lot_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const pressure_test_casing_liner_equinor = {
  label: 'Pressure Test Casing/Liner',
  key: 'pressure_test_casing_liner_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const land_test_casing_hanger_equinor = {
  label: 'Land & Test Casing/Tubing Hanger',
  key: 'land_test_casing_hanger_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_wireline_open_hole_equinor = {
  label: 'M/U Wireline - Open Hole',
  key: 'mu_wireline_open_hole_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_wireline_open_hole_equinor = {
  label: 'L/D Wireline - Open Hole',
  key: 'ld_wireline_open_hole_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_wireline_cased_hole_equinor = {
  label: 'M/U Wireline - Cased Hole',
  key: 'mu_wireline_cased_hole_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_wireline_cased_hole_equinor = {
  label: 'L/D Wireline - Cased Hole',
  key: 'ld_wireline_cased_hole_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_shoetrack_equinor = {
  label: 'M/U Shoetrack',
  key: 'mu_shoetrack_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const logging_time_ch_equinor = {
  label: 'Logging Time CH',
  key: 'logging_time_ch_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const logging_time_oh_equinor = {
  label: 'Logging Time OH',
  key: 'logging_time_oh_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_hanger_equinor = {
  label: 'M/U Hanger',
  key: 'mu_hanger_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const cementing_time_equinor = {
  label: 'Cementing Time',
  key: 'cementing_time_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const prepare_for_cementing_equinor = {
  label: 'Prepare for cementing',
  key: 'prepare_for_cementing_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const run_landing_string_equinor = {
  label: 'Run Landing String',
  key: 'run_landing_string_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const pull_landing_string_equinor = {
  label: 'Pull Landing String',
  key: 'pull_landing_string_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const prep_to_pooh_equinor = {
  label: 'Prep to POOH',
  key: 'prep_to_pooh_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const nipple_up_bop_equinor = {
  label: 'Nipple Up BOP',
  key: 'nipple_up_bop_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const nipple_down_bop_equinor = {
  label: 'Nipple Down BOP',
  key: 'nipple_down_bop_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_cement_stand_equinor = {
  label: 'M/U Cement Stand',
  key: 'mu_cement_stand_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_cement_stand_equinor = {
  label: 'L/D Cement Stand',
  key: 'ld_cement_stand_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_dhpg_equinor = {
  label: 'M/U DHPG',
  key: 'mu_dhpg_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_dhpg_equinor = {
  label: 'L/D DHPG',
  key: 'ld_dhpg_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_dhsv_equinor = {
  label: 'M/U DHSV',
  key: 'mu_dhsv_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_dhsv_equinor = {
  label: 'L/D DHSV',
  key: 'ld_dhsv_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const mu_gaslift_equipment_equinor = {
  label: 'M/U Gaslift Equipment',
  key: 'mu_gaslift_equipment_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const ld_gaslift_equipment_equinor = {
  label: 'L/D Gaslift Equipment',
  key: 'ld_gaslift_equipment_equinor',
  unitType: 'time',
  from: 's',
  to: 'h',
};

export const tripping_in_connection_time_open_average = {
  label: 'Open Hole Tripping In Connection Time',
  key: 'tripping_in_connection_time_open_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_in_connection_time_cased_average = {
  label: 'Cased Hole Tripping In Connection Time',
  key: 'tripping_in_connection_time_cased_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_in_connection_average = {
  label: 'Entire Hole Tripping In Connection Time',
  key: 'tripping_in_connection_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_out_connection_time_open_average = {
  label: 'Open Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_time_open_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_out_connection_time_cased_average = {
  label: 'Cased Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_time_cased_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const tripping_out_connection_average = {
  label: 'Entire Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_average',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
};

export const total_variance_hal = {
  label: 'Total Variance',
  key: 'total_variance_hal',
  isBiggestBest: false,
  to: 'h',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.drilling_group,
  allowNegativeValue: true,
};

export const target_formation = {
  label: 'Target Formation',
  key: 'formation',
};

export const mud_type = {
  label: 'Mud Type',
  key: 'mud_type',
};

export const tripping_in_speed_with_connection_landingstring = {
  label: 'Landing String - Entire Hole Tripping In Speed (With Connection)',
  key: 'tripping_in_speed_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_speed_open_with_connection_landingstring = {
  label: 'Landing String - Open Hole Tripping In Speed (With Connection)',
  key: 'tripping_in_speed_open_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_speed_cased_with_connection_landingstring = {
  label: 'Landing String - Cased Hole Tripping In Speed (With Connection)',
  key: 'tripping_in_speed_cased_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_with_connection_landingstring = {
  label: 'Landing String - Entire Hole Tripping Out Speed (With Connection)',
  key: 'tripping_out_speed_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_open_with_connection_landingstring = {
  label: 'Landing String - Open Hole Tripping Out Speed (With Connection)',
  key: 'tripping_out_speed_open_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_cased_with_connection_landingstring = {
  label: 'Landing String - Cased Hole Tripping Out Speed (With Connection)',
  key: 'tripping_out_speed_cased_with_connection_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_speed_landingstring = {
  label: 'Landing String - Entire Hole Tripping In Speed',
  key: 'tripping_in_speed_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_speed_open_landingstring = {
  label: 'Landing String - Open Hole Tripping In Speed',
  key: 'tripping_in_speed_open_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_speed_cased_landingstring = {
  label: 'Landing String - Cased Hole Tripping In Speed',
  key: 'tripping_in_speed_cased_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_landingstring = {
  label: 'Landing String - Entire Hole Tripping Out Speed',
  key: 'tripping_out_speed_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_open_landingstring = {
  label: 'Landing String - Open Hole Tripping Out Speed',
  key: 'tripping_out_speed_open_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_speed_cased_landingstring = {
  label: 'Landing String - Cased Hole Tripping Out Speed',
  key: 'tripping_out_speed_cased_landingstring',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_connection_time_landingstring = {
  label: 'Landing String - Entire Hole Tripping In Connection Time',
  key: 'tripping_in_connection_time_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_connection_time_open_landingstring = {
  label: 'Landing String - Open Hole Tripping In Connection Time',
  key: 'tripping_in_connection_time_open_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_in_connection_time_cased_landingstring = {
  label: 'Landing String - Cased Hole Tripping In Connection Time',
  key: 'tripping_in_connection_time_cased_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_connection_time_landingstring = {
  label: 'Landing String - Entire Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_time_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_connection_time_open_landingstring = {
  label: 'Landing String - Open Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_time_open_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const tripping_out_connection_time_cased_landingstring = {
  label: 'Landing String - Cased Hole Tripping Out Connection Time',
  key: 'tripping_out_connection_time_cased_landingstring',
  to: 'min',
  from: 's',
  unitType: 'time',
  convertForMultiRigs: getAverage,
  category: metricsCategories.landing_string_group,
};

export const gross_tripping_in_speed_bha_tubing_single = {
  label: 'Single Tubing Tripping In Speed',
  key: 'gross_tripping_in_speed_bha_tubing_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_in_speed_bha_tubing_double = {
  label: 'Double Tubing Tripping In Speed',
  key: 'gross_tripping_in_speed_bha_tubing_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

/** @deprecated Use gross_tripping_in_speed_rod_single instead */
export const gross_tripping_in_speed_bha_rod_single = {
  label: 'Single Rod Tripping In Speed',
  key: 'gross_tripping_in_speed_bha_rod_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_in_speed_rod_single = {
  label: 'Single Rod Tripping In Speed',
  key: 'gross_tripping_in_speed_rod_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

/** @deprecated Use gross_tripping_in_speed_rod_double instead */
export const gross_tripping_in_speed_bha_rod_double = {
  label: 'Double Rod Tripping In Speed',
  key: 'gross_tripping_in_speed_bha_rod_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_in_speed_rod_double = {
  label: 'Double Rod Tripping In Speed',
  key: 'gross_tripping_in_speed_rod_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const tripping_in_speed_std_hr = {
  label: 'BHA tripping in speed with connections',
  key: 'tripping_in_speed_std_hr',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
  unitType: null,
  unitDisplay: 'stands/h',
};

export const gross_tripping_out_speed_bha_tubing_single = {
  label: 'Single Tubing Tripping Out Speed',
  key: 'gross_tripping_out_speed_bha_tubing_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_out_speed_bha_tubing_double = {
  label: 'Double Tubing Tripping Out Speed',
  key: 'gross_tripping_out_speed_bha_tubing_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

/** @deprecated Use gross_tripping_out_speed_rod_single instead */
export const gross_tripping_out_speed_bha_rod_single = {
  label: 'Single Rod Tripping Out Speed',
  key: 'gross_tripping_out_speed_bha_rod_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_out_speed_rod_single = {
  label: 'Single Rod Tripping Out Speed',
  key: 'gross_tripping_out_speed_rod_single',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

/** @deprecated Use gross_tripping_out_speed_rod_double instead */
export const gross_tripping_out_speed_bha_rod_double = {
  label: 'Double Rod Tripping Out Speed',
  key: 'gross_tripping_out_speed_bha_rod_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const gross_tripping_out_speed_rod_double = {
  label: 'Double Rod Tripping Out Speed',
  key: 'gross_tripping_out_speed_rod_double',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: 'velocity',
  from: 'ft/h',
};

export const tripping_out_speed_std_hr = {
  label: 'BHA tripping out speed with connections',
  key: 'tripping_out_speed_std_hr',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
  unitType: null,
  unitDisplay: 'stands/h',
};

export const tripping_connection_average = {
  label: 'Tripping Connection Average Time',
  key: 'tripping_connection_average',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_connection_group,
  to: 'min',
  from: 's',
  unitType: 'time',
};

export const tripping_connection_time = {
  label: 'Tripping Connection Total Time',
  key: 'tripping_connection_time',
  convertForMultiRigs: getSum,
  category: metricsCategories.tripping_connection_group,
  to: 'min',
  from: 's',
  unitType: 'time',
};

export const tripping_speed = {
  label: 'Tripping Speed',
  key: 'tripping_speed',
  unitType: 'velocity',
  from: 'ft/h',
  isBiggestBest: true,
  convertForMultiRigs: getAverage,
};

export const tripping_out_time = {
  label: 'Tripping Out Time',
  key: 'tripping_out_time',
  unitType: 'time',
  from: 's',
  to: 'h',
  convertForMultiRigs: getSum,
};

export const gross_tripping_in_speed_bha_std_hr = {
  label: 'BHA tripping in speed with connections',
  key: 'gross_tripping_in_speed_bha_std_hr',
  unitType: null,
  unitDisplay: 'stands/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_in_speed_group,
};

export const gross_tripping_out_speed_bha_std_hr = {
  label: 'BHA tripping out speed with connections',
  key: 'gross_tripping_out_speed_bha_std_hr',
  unitType: null,
  unitDisplay: 'stands/h',
  convertForMultiRigs: getAverage,
  category: metricsCategories.tripping_out_speed_group,
};

export const gross_tripping_in_time = {
  label: 'Total tripping in time — pipe-moving with connections',
  key: 'gross_tripping_in_time',
  unitType: 'time',
  from: 's',
  to: 'h',
  convertForMultiRigs: getSum,
};

export const gross_tripping_out_time = {
  label: 'Total tripping out time — pipe-moving with connections',
  key: 'gross_tripping_out_time',
  unitType: 'time',
  from: 's',
  to: 'h',
  convertForMultiRigs: getSum,
};
